import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productService } from '../services/productService';
import { userService } from '../services/userService';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import ProductImageGallery from '../components/product/ProductImageGallery';
import RelatedProducts from '../components/product/RelatedProducts';
import StarRating from '../components/rating/StarRating';
import ProductReviews from '../components/rating/ProductReviews';
import Loading from '../components/common/Loading';
import Dialog from '../components/common/Dialog';
import SEO from '../components/common/SEO';
import { formatPrice } from '../utils/helpers';
import { buildWhatsAppOrderUrl } from '../utils/whatsapp';
import './ProductDetail.css';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const {
    loading: settingsLoading,
    whatsappOrdering,
    whatsappNumber,
  } = useSettings();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const [cartFeedback, setCartFeedback] = useState('');
  const [cartError, setCartError] = useState('');
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [ratingSummary, setRatingSummary] = useState(null);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const { product: productData, relatedProducts: related } =
          await productService.getProductBySlug(slug);

        setProduct(productData);
        setRelatedProducts(Array.isArray(related) ? related : []);
        setRatingSummary({
          average: productData?.rating_average ?? null,
          count: productData?.rating_count || 0,
        });
        // Pre-select the first color so the swatch UI is always primed.
        if (
          Array.isArray(productData?.colors) &&
          productData.colors.length > 0
        ) {
          const sorted = [...productData.colors].sort(
            (a, b) => (a.display_order || 0) - (b.display_order || 0)
          );
          setSelectedColorId(sorted[0].id);
        } else {
          setSelectedColorId(null);
        }
      } catch (err) {
        setError(err.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadProduct();
    }
  }, [slug]);

  // Load favorite state for this product when user and product are available
  useEffect(() => {
    const loadFavoriteState = async () => {
      if (!isAuthenticated || !product?.id) {
        setIsFavorite(false);
        return;
      }

      try {
        const data = await userService.getFavorites();
        const favorites = Array.isArray(data) ? data : data?.favorites || [];
        const isFav = favorites.some(p => p.id === product.id);
        setIsFavorite(isFav);
      } catch (e) {
        // Silently ignore errors; user can still toggle manually
      }
    };

    loadFavoriteState();
  }, [isAuthenticated, product?.id]);

  // The button goes where the shop's ordering mode says it goes, so it says so
  // too: "Add to Cart" only when it really does add to a cart.
  const buttonLabel = whatsappOrdering
    ? 'Shop Now'
    : addingToCart
      ? 'Adding...'
      : 'Add to Cart';

  const unavailableMessage =
    'Ordering is temporarily unavailable. Please reach us from the Help & Support page.';

  const productUrl = typeof window !== 'undefined' ? window.location.href : '';

  // The shop-side failure (no WhatsApp number configured) gets a route to a
  // human; the customer-side ones ("pick a colour") are just a nudge.
  const isOrderingUnavailable = cartError === unavailableMessage;

  // While the shop takes orders on WhatsApp, the button hands the customer to
  // its chat with the product prefilled instead of writing to the cart. The
  // number arrives with the settings, so this stays synchronous and the tab
  // opens inside the click's own gesture, surviving every popup blocker.
  const handleWhatsAppOrder = colorName => {
    const url = buildWhatsAppOrderUrl({
      number: whatsappNumber,
      product,
      colorName,
      productUrl,
    });

    if (!url) {
      setCartFeedback('');
      setCartError(unavailableMessage);
      return;
    }

    setCartError('');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleAddToCart = async () => {
    if (!whatsappOrdering && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (product.stock_quantity === 0) {
      setCartError('This product is out of stock');
      setCartFeedback('');
      return;
    }

    const hasColors =
      Array.isArray(product.colors) && product.colors.length > 0;
    if (hasColors && !selectedColorId) {
      setCartError('Please select a color');
      setCartFeedback('');
      return;
    }

    if (whatsappOrdering) {
      const selectedColor = hasColors
        ? product.colors.find(c => c.id === selectedColorId)
        : null;
      handleWhatsAppOrder(selectedColor?.name);
      return;
    }

    try {
      setAddingToCart(true);
      await addToCart(product.id, 1, hasColors ? selectedColorId : null);
      setCartError('');
      setCartFeedback('Product added to your cart');
      setTimeout(() => {
        setCartFeedback('');
      }, 3000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Failed to add product to cart';
      setCartFeedback('');
      setCartError(errorMessage);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && productUrl) {
        await navigator.clipboard.writeText(productUrl);
        setCopyStatus('Link copied!');
      } else if (productUrl) {
        const textarea = document.createElement('textarea');
        textarea.value = productUrl;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopyStatus('Link copied!');
      }
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (e) {
      setCopyStatus('Could not copy link');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!product) return;

    try {
      if (isFavorite) {
        await userService.removeFavorite(product.id);
        setIsFavorite(false);
      } else {
        await userService.addFavorite(product.id);
        setIsFavorite(true);
      }
    } catch (err) {
      // Keep UI state unchanged on error
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update favorites';
      alert(message);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error || !product) {
    return <div className="error-message">{error || 'Product not found'}</div>;
  }

  // Prepare structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    image: product.images?.map(img => img.image_url) || [product.image_url],
    description: product.description?.replace(/<[^>]*>/g, ''), // Strip HTML for SEO
    brand: {
      '@type': 'Brand',
      name: 'Essentick',
    },
    offers: {
      '@type': 'Offer',
      url: window.location.href,
      priceCurrency: 'NPR',
      price: product.discounted_price || product.price,
      availability:
        product.stock_quantity > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
    },
  };

  const sortedColors = Array.isArray(product.colors)
    ? [...product.colors].sort(
        (a, b) => (a.display_order || 0) - (b.display_order || 0)
      )
    : [];
  const selectedColor = sortedColors.find(c => c.id === selectedColorId);

  // The API already sorts these, but it is cheap to be certain the list does not
  // depend on include-ordering to read correctly.
  const highlights = Array.isArray(product.highlights)
    ? [...product.highlights].sort(
        (a, b) => (a.display_order || 0) - (b.display_order || 0)
      )
    : [];

  return (
    <div className="product-detail">
      <SEO
        title={product.name}
        description={
          product.description?.replace(/<[^>]*>/g, '').substring(0, 160) ||
          `Buy ${product.name} at Essentick. Premium quality watches and lifestyle products in Nepal.`
        }
        image={product.images?.[0]?.url || product.image_url || '/logo512.png'}
        url={`/products/${slug}`}
        type="product"
        keywords={`${product.name}, ${product.category?.name || 'watches'}, Essentick, Nepal`}
        structuredData={structuredData}
      />
      <div className="product-detail-container">
        <div className="product-detail-images-wrapper">
          <ProductImageGallery images={product.images} />
        </div>

        <div className="product-detail-info">
          <nav className="product-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link> » <Link to="/products">Shop</Link> »{' '}
            <span>{product.name}</span>
          </nav>
          <h1>{product.name}</h1>
          {/* Hidden entirely until the product has at least one review. */}
          {ratingSummary?.count > 0 && (
            <a href="#product-reviews" className="product-rating-link">
              <StarRating value={ratingSummary.average} size="md" />
              <span className="product-rating-value">
                {ratingSummary.average}
              </span>
              <span className="product-rating-count">
                ({ratingSummary.count}{' '}
                {ratingSummary.count === 1 ? 'review' : 'reviews'})
              </span>
            </a>
          )}
          <div className="product-price-container">
            {product.discounted_price ? (
              <>
                <span className="product-price discounted">
                  {formatPrice(product.discounted_price)}
                </span>
                <span className="product-price original">
                  {formatPrice(product.price)}
                </span>
                <span className="discount-tag">
                  {Math.round(
                    ((Number(product.price) -
                      Number(product.discounted_price)) /
                      Number(product.price)) *
                      100
                  )}
                  % Off !
                </span>
              </>
            ) : (
              <span className="product-price">
                {formatPrice(product.price)}
              </span>
            )}
          </div>

          <div className="stock-status-container">
            <span
              className={
                product.stock_quantity > 0
                  ? 'stock-status in-stock'
                  : 'stock-status out-of-stock'
              }
            >
              {product.stock_quantity > 0 ? 'In Stock' : 'Out of Stock'}
            </span>
          </div>

          {highlights.length > 0 && (
            <div className="product-highlights-box">
              <strong className="highlights-title">Highlights:</strong>
              <ul className="highlights-list">
                {highlights.map(highlight => (
                  <li key={highlight.id}>
                    {highlight.label ? (
                      <>
                        <span className="highlight-label">
                          {highlight.label}:
                        </span>{' '}
                        {highlight.value}
                      </>
                    ) : (
                      highlight.value
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sortedColors.length > 0 && (
            <div className="product-colors">
              <div className="product-colors-label">
                Color{selectedColor ? `: ${selectedColor.name}` : ''}
              </div>
              <div className="product-colors-swatches">
                {sortedColors.map(c => {
                  const isSelected = c.id === selectedColorId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`color-swatch${isSelected ? ' selected' : ''}`}
                      style={{ backgroundColor: c.hex_code }}
                      title={c.name}
                      aria-label={c.name}
                      aria-pressed={isSelected}
                      onClick={() => setSelectedColorId(c.id)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div className="product-actions-group">
            <button
              className="btn-add-to-cart orange-btn"
              onClick={handleAddToCart}
              disabled={
                addingToCart || settingsLoading || product.stock_quantity === 0
              }
            >
              <span className="btn-text">{buttonLabel}</span>
            </button>

            <div className="product-actions-secondary">
              <button
                type="button"
                className={`icon-button favorite-button ${isFavorite ? 'active' : ''}`}
                onClick={toggleFavorite}
                aria-label={
                  isFavorite ? 'Remove from favorites' : 'Add to favorites'
                }
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill={isFavorite ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
                </svg>
              </button>

              <button
                type="button"
                className="icon-button share-button"
                onClick={() => setIsShareOpen(true)}
                aria-label="Share product"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
                  <line x1="15.4" y1="6.5" x2="8.6" y2="10.5" />
                </svg>
              </button>
            </div>
          </div>

          {cartFeedback && (
            <div className="cart-feedback-message cart-feedback-success">
              {cartFeedback}
            </div>
          )}

          <div className="delivery-viewers-container">
            <div className="info-line delivery-estimate">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>
                <strong>Estimated Delivery:</strong>{' '}
                {(() => {
                  const now = new Date();
                  const start = new Date(now);
                  start.setDate(now.getDate() + 1);
                  const end = new Date(now);
                  end.setDate(now.getDate() + 3);
                  const fmt = d =>
                    d.toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'long',
                    });
                  return `${fmt(start)} - ${fmt(end)}`;
                })()}
              </span>
            </div>
          </div>
        </div>

        <div className="product-description-row">
          <h3>Product Description</h3>
          {product.description ? (
            <div
              className="description-content"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
          ) : (
            <div className="description-placeholder">
              <p>
                Experience the perfect blend of style and functionality with{' '}
                {product.name}. Carefully crafted for those who appreciate
                premium quality and authentic design.
              </p>
              <ul>
                <li>Authentic craftsmanship</li>
                <li>Premium materials</li>
                <li>Warranty included</li>
                <li>Exclusive limited edition</li>
              </ul>
            </div>
          )}
        </div>

        <div className="product-reviews-row" id="product-reviews">
          <ProductReviews
            productSlug={slug}
            productId={product.id}
            onSummaryChange={setRatingSummary}
          />
        </div>

        <div className="product-related-row">
          <RelatedProducts products={relatedProducts} />
        </div>
      </div>

      {isShareOpen && (
        <div
          className="share-dialog-backdrop"
          onClick={() => setIsShareOpen(false)}
        >
          <div
            className="share-dialog"
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <div className="share-dialog-header">
              <h4>Share this product</h4>
              <button
                type="button"
                className="icon-button share-dialog-close"
                onClick={() => setIsShareOpen(false)}
                aria-label="Close share dialog"
              >
                ×
              </button>
            </div>

            <div className="share-dialog-body">
              <div className="share-link-row">
                <input
                  type="text"
                  readOnly
                  value={productUrl}
                  className="share-link-input"
                />
                <button
                  type="button"
                  className="btn-copy-link"
                  onClick={handleCopyLink}
                >
                  Copy link
                </button>
              </div>
              {copyStatus && (
                <div className="share-copy-status">{copyStatus}</div>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={!!cartError}
        title={
          isOrderingUnavailable ? 'Ordering unavailable' : 'One more thing'
        }
        message={cartError}
        onClose={() => setCartError('')}
        actions={
          isOrderingUnavailable ? (
            <Link to="/support" className="dialog-button">
              Help &amp; Support
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default ProductDetail;
